import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AnnouncementBar from '@/components/feature/AnnouncementBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/feature/ProductCard';
import FilterSidebar from './components/FilterSidebar';
import SortBar from './components/SortBar';
import { products } from '@/mocks/products';
import { categories } from '@/mocks/categories';

export default function Shop() {
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortValue, setSortValue] = useState('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [inStockOnly, setInStockOnly] = useState(false);

  // Initialize category from URL
  const urlCategory = useMemo(() => {
    if (!categorySlug) return null;
    const cat = categories.find((c) => c.slug === categorySlug);
    return cat?.id ?? null;
  }, [categorySlug]);

  const activeCategoryId = selectedCategory ?? urlCategory;

  const categoryName = useMemo(() => {
    if (!activeCategoryId) return 'All Products';
    const cat = categories.find((c) => c.id === activeCategoryId);
    return cat?.name ?? 'All Products';
  }, [activeCategoryId]);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Category filter
    if (activeCategoryId) {
      result = result.filter((p) => p.categoryId === activeCategoryId);
    }

    // Brand filter
    if (selectedBrands.length > 0) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }

    // Price filter
    const min = priceMin ? parseInt(priceMin, 10) : 0;
    const max = priceMax ? parseInt(priceMax, 10) : Infinity;
    result = result.filter((p) => {
      const price = p.salePrice ?? p.price;
      return price >= min && price <= max;
    });

    // Rating filter
    if (selectedRating) {
      result = result.filter((p) => p.rating >= selectedRating);
    }

    // Stock filter
    if (inStockOnly) {
      result = result.filter((p) => p.stock > 0);
    }

    // Sort
    switch (sortValue) {
      case 'price_asc':
        result.sort((a, b) => (a.salePrice ?? a.price) - (b.salePrice ?? b.price));
        break;
      case 'price_desc':
        result.sort((a, b) => (b.salePrice ?? b.price) - (a.salePrice ?? a.price));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'bestselling':
        result.sort((a, b) => b.soldCount - a.soldCount);
        break;
      default:
        break;
    }

    return result;
  }, [activeCategoryId, selectedBrands, priceMin, priceMax, selectedRating, inStockOnly, sortValue]);

  const toggleBrand = (brand: string) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand],
    );
  };

  return (
    <div className="min-h-screen bg-background-50">
      <AnnouncementBar />
      <Navbar />

      <main className="pt-28 md:pt-36 pb-12">
        <div className="w-full px-4 md:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-4">
            <a href="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Home
            </a>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <a href="/shop" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Shop
            </a>
            {activeCategoryId && (
              <>
                <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
                <span className="text-foreground-900 font-medium">{categoryName}</span>
              </>
            )}
          </nav>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
            {categoryName}
          </h1>

          {/* Mobile filter toggle */}
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors"
            >
              <i className="ri-filter-3-line" />
              Filters
              {(selectedBrands.length > 0 || priceMin || priceMax || selectedRating || selectedCategory || inStockOnly) && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                  {selectedBrands.length + (priceMin || priceMax ? 1 : 0) + (selectedRating ? 1 : 0) + (selectedCategory ? 1 : 0) + (inStockOnly ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          <div className="flex gap-6">
            {/* Sidebar - Desktop */}
            <div className="hidden lg:block">
              <div className="sticky top-36">
                <FilterSidebar
                  selectedBrands={selectedBrands}
                  onBrandToggle={toggleBrand}
                  priceMin={priceMin}
                  priceMax={priceMax}
                  onPriceMinChange={setPriceMin}
                  onPriceMaxChange={setPriceMax}
                  selectedRating={selectedRating}
                  onRatingChange={setSelectedRating}
                  selectedCategory={selectedCategory ?? urlCategory ?? null}
                  onCategoryChange={(cat) => {
                    setSelectedCategory(cat);
                  }}
                  inStockOnly={inStockOnly}
                  onInStockToggle={() => setInStockOnly((v) => !v)}
                />
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <SortBar
                sortValue={sortValue}
                onSortChange={setSortValue}
                resultCount={filteredProducts.length}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />

              {filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-search-line text-2xl text-foreground-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-800 mb-1">No products found</h3>
                  <p className="text-sm text-foreground-500">Try adjusting your filters to see more results.</p>
                </div>
              ) : (
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-4'
                      : 'flex flex-col gap-3 pt-4'
                  }
                >
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Filter Panel */}
      {mobileFiltersOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-background-50 shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200/60">
              <h3 className="text-base font-bold text-foreground-950">Filters</h3>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
              >
                <i className="ri-close-line text-lg text-foreground-700" />
              </button>
            </div>
            <div className="p-4">
              <FilterSidebar
                selectedBrands={selectedBrands}
                onBrandToggle={toggleBrand}
                priceMin={priceMin}
                priceMax={priceMax}
                onPriceMinChange={setPriceMin}
                onPriceMaxChange={setPriceMax}
                selectedRating={selectedRating}
                onRatingChange={setSelectedRating}
                selectedCategory={selectedCategory ?? urlCategory ?? null}
                onCategoryChange={(cat) => {
                  setSelectedCategory(cat);
                }}
                inStockOnly={inStockOnly}
                onInStockToggle={() => setInStockOnly((v) => !v)}
              />
            </div>
            <div className="p-4 border-t border-background-200/60">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
              >
                Show {filteredProducts.length} Results
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}